// @storybook-skip: internal section of LessonsExploreScreen; covered by its screen story
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, type StyleProp, type ViewStyle } from "react-native";
import Box from "../Box";
import EmptyState from "../EmptyView";
import { LessonExploreCard } from "./LessonExploreCard";
import { getExploreEmptyState } from "./helpers";
import type {
	ExploreLesson,
	LessonStatusFilter,
	LessonViewMode,
} from "./types";

type Props = {
	lessons: ExploreLesson[];
	viewMode: LessonViewMode;
	isMobile: boolean;
	statusFilter: LessonStatusFilter;
	totalLessons: number;
	onLessonPress: (lesson: ExploreLesson) => void;
	onClearFilters: () => void;
	contentContainerStyle?: StyleProp<ViewStyle>;
};

export const LessonsExploreList = ({
	lessons,
	viewMode,
	isMobile,
	statusFilter,
	totalLessons,
	onLessonPress,
	onClearFilters,
	contentContainerStyle,
}: Props) => {
	const { t } = useTranslation();
	const numColumns = viewMode === "grid" && !isMobile ? 2 : 1;
	const cardLayout = isMobile || numColumns > 1 ? "stacked" : "row";

	const renderItem = useCallback(
		({ item }: { item: ExploreLesson }) => (
			<Box
				flex={1}
				maxWidth={numColumns > 1 ? "50%" : "100%"}
				paddingBottom={isMobile ? 8 : 16}
			>
				<LessonExploreCard
					lesson={item}
					layout={cardLayout}
					onPress={onLessonPress}
				/>
			</Box>
		),
		[cardLayout, isMobile, numColumns, onLessonPress],
	);

	const keyExtractor = useCallback((item: ExploreLesson) => item.id, []);

	const emptyState = useMemo(
		() => getExploreEmptyState({ t, statusFilter, totalLessons }),
		[statusFilter, t, totalLessons],
	);

	return (
		<FlatList
			key={numColumns}
			data={lessons}
			numColumns={numColumns}
			style={{ flex: 1, width: "100%" }}
			contentContainerStyle={
				lessons.length
					? contentContainerStyle
					: [contentContainerStyle, { flexGrow: 1, paddingBottom: 0 }]
			}
			columnWrapperStyle={numColumns > 1 ? { gap: 16 } : undefined}
			keyExtractor={keyExtractor}
			renderItem={renderItem}
			ListEmptyComponent={
				<Box
					flex={1}
					justifyContent="center"
					paddingBottom={24}
					paddingHorizontal={isMobile ? 16 : 0}
				>
					<EmptyState
						title={emptyState.title}
						subtitle={emptyState.subtitle}
						cta={emptyState.cta}
						handleAction={onClearFilters}
					/>
				</Box>
			}
		/>
	);
};
